import React, { useState, useEffect, useMemo } from 'react';
import {  Container, 
  Typography, 
  Box,
  Paper,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Divider,
  Button,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar
} from '@mui/material';
import { format } from 'date-fns';
import PrintIcon from '@mui/icons-material/Print';
import FilterListIcon from '@mui/icons-material/FilterList';
import WarningIcon from '@mui/icons-material/Warning';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PersonIcon from '@mui/icons-material/Person';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import DocumentScanner from '@mui/icons-material/DocumentScanner';
import Close from '@mui/icons-material/Close';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import BusinessIcon from '@mui/icons-material/Business';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import DescriptionIcon from '@mui/icons-material/Description';
import * as XLSX from 'xlsx';

// Update docx imports with aliases to avoid conflicts with MUI components
import { Document, Packer, Paragraph, Table as DocxTable, TableRow as DocxTableRow, TableCell as DocxTableCell, HeadingLevel, AlignmentType, PageOrientation, WidthType, BorderStyle } from 'docx';
import { saveAs } from 'file-saver';

import { 
  getJobsForAdminDashboard
} from '../services/api';
import { AdminDashboardJob } from '../models/types';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

// Interfaces - updated to use AdminDashboard types
interface Volunteer {
  id: string;
  name: string;
  email: string;
  phoneNumber?: string;
  commodity?: string;
  accessToken?: string;
  totalMinutesWorked?: number;
  earnedGoodie?: boolean;
}

interface Timeslot {
  id: string;
  dateFormatted: string;
  timeFormatted: string;
  description?: string;
  requiredVolunteers: number;
  filledSpots: number;
  volunteers: Volunteer[];
  shortfall: number;
}

// Help format minutes to hours and minutes
const formatMinutes = (minutes: number): string => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours}h ${mins}m`;
};

// Group timeslots into sets of 3 for printing
const groupTimeslots = (timeslots: Timeslot[]) => {
  const groups = [];
  for (let i = 0; i < timeslots.length; i += 3) {
    groups.push(timeslots.slice(i, i + 3));
  }
  return groups;
};

const AdminDashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated, isAdmin } = useAuth();
  
  // State
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [jobs, setJobs] = useState<AdminDashboardJob[]>([]);
  
  // Filter state
  const [jobFilter, setJobFilter] = useState<string>('all');
  const [dayFilter, setDayFilter] = useState<string>('all');
  const [coverageFilter, setCoverageFilter] = useState<string>('all');
  const [availableDays, setAvailableDays] = useState<string[]>([]);
  
  // Dialog state for volunteer details
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedVolunteers, setSelectedVolunteers] = useState<Volunteer[]>([]);
  const [selectedTimeslotInfo, setSelectedTimeslotInfo] = useState<{jobName: string, timeslot: string} | null>(null);
  
  // Print modes
  const [printMode, setPrintMode] = useState(false);
  const [rosterPrintMode, setRosterPrintMode] = useState(false);
  
  // Fetch all necessary data - OPTIMIZED VERSION
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Single optimized API call instead of N+1 pattern
        const jobsData = await getJobsForAdminDashboard();
        
        // Extract unique dates for the day filter
        const allDates = new Set<string>();
        jobsData.forEach(job => {
          job.timeslots.forEach(ts => {
            allDates.add(ts.dateFormatted);
          });
        });
        
        // Sort dates chronologically
        const sortedDates = Array.from(allDates).sort();
        
        setJobs(jobsData);
        setAvailableDays(sortedDates);
        setError(null);
      } catch (err) {
        console.error('Error fetching dashboard data:', err);
        setError('Failed to load dashboard data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    // Only fetch if user is admin
    if (isAuthenticated && isAdmin) {
      fetchData();
    } else if (isAuthenticated && !isAdmin) {
      navigate('/volunteer/dashboard');
    } else if (!isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, isAdmin, navigate]);
  
  // Apply filters
  const filteredJobs = useMemo(() => {
    let result = [...jobs];
    
    // Apply job filter
    if (jobFilter !== 'all') {
      result = result.filter(job => job.id === jobFilter);
    }
    
    // Apply coverage filter
    if (coverageFilter === 'critical') {
      result = result.filter(job => job.coverage < 50);
    } else if (coverageFilter === 'warning') {
      result = result.filter(job => job.coverage >= 50 && job.coverage < 80);
    } else if (coverageFilter === 'good') {
      result = result.filter(job => job.coverage >= 80);
    }
    
    // If day filter is active, we need to filter timeslots within jobs
    if (dayFilter !== 'all') {
      result = result.map(job => ({
        ...job,
        timeslots: job.timeslots.filter(ts => ts.dateFormatted === dayFilter)
      })).filter(job => job.timeslots.length > 0);
    }
    
    return result;
  }, [jobs, jobFilter, dayFilter, coverageFilter]);
  
  // Calculate overall statistics
  const overallStats = useMemo(() => {
    const totalRequiredVolunteers = jobs.reduce(
      (sum, job) => sum + job.requiredVolunteerCount, 0);
      
    const totalRegisteredVolunteers = jobs.reduce(
      (sum, job) => sum + job.registeredVolunteerCount, 0);
      
    const totalMinutesRequired = jobs.reduce(
      (sum, job) => sum + job.totalMinutesRequired, 0);
      
    const totalMinutesCommitted = jobs.reduce(
      (sum, job) => sum + job.totalMinutesCommitted, 0);
      
    const overallCoverage = totalRequiredVolunteers > 0 
      ? (totalRegisteredVolunteers / totalRequiredVolunteers) * 100 
      : 100;
      
    const criticalJobs = jobs.filter(job => job.coverage < 50).length;
    
    return {
      totalRequiredVolunteers,
      totalRegisteredVolunteers,
      totalHoursRequired: totalMinutesRequired / 60,
      totalHoursCommitted: totalMinutesCommitted / 60,
      overallCoverage,
      criticalJobs
    };
  }, [jobs]);

  
  // Handle Excel export
  const handleExcelExport = () => {
    try {
      const workbook = XLSX.utils.book_new();
      
      // Create overview sheet with enhanced layout
      const overviewData = [
        ['STRUDENGAUER MESSE - HELFER ÜBERSICHT', '', '', '', '', '', '', ''],
        [`Erstellt am: ${format(new Date(), 'dd.MM.yyyy HH:mm')}`, '', '', '', '', '', '', ''],
        ['', '', '', '', '', '', '', ''],
        ['📊 GESAMTSTATISTIKEN', '', '', '', '', '', '', ''],
        ['', '', '', '', '', '', '', ''],
        ['Gesamt-Abdeckung:', `${Math.round(overallStats.overallCoverage)}%`, '', 'Kritische Dienste:', overallStats.criticalJobs, '', 'Status:', overallStats.overallCoverage >= 80 ? '✅ Gut' : overallStats.overallCoverage >= 50 ? '⚠️ Warnung' : '🔴 Kritisch'],
        ['Positionen besetzt:', `${overallStats.totalRegisteredVolunteers} von ${overallStats.totalRequiredVolunteers}`, '', 'Benötigte Stunden:', `${overallStats.totalHoursRequired.toFixed(1)}h`, '', 'Zugesagte Stunden:', `${overallStats.totalHoursCommitted.toFixed(1)}h`],
        ['', '', '', '', '', '', '', ''],
        ['Progress-Visualisierung:', '█'.repeat(Math.round(overallStats.overallCoverage / 5)) + '░'.repeat(20 - Math.round(overallStats.overallCoverage / 5)), `${Math.round(overallStats.overallCoverage)}%`, '', '', '', '', ''],
        ['', '', '', '', '', '', '', ''],
        ['📋 JOB ÜBERSICHT', '', '', '', '', '', '', ''],
        ['Job Name', 'Ort', 'Abdeckung', 'Status', 'Benötigt', 'Angemeldet', 'Fehlend', 'Progress Bar'],
      ];
      
      // Add job summary data with visual indicators
      filteredJobs.forEach(job => {
        const coverage = Math.round(job.coverage);
        const statusEmoji = coverage >= 80 ? '✅' : coverage >= 50 ? '⚠️' : '🔴';
        const statusText = coverage >= 80 ? 'Gut' : coverage >= 50 ? 'Warnung' : 'Kritisch';
        const progressBar = '█'.repeat(Math.round(coverage / 5)) + '░'.repeat(20 - Math.round(coverage / 5));
        
        overviewData.push([
          job.name,
          job.location,
          `${coverage}%`,
          `${statusEmoji} ${statusText}`,
          job.requiredVolunteerCount.toString(),
          job.registeredVolunteerCount.toString(),
          job.shortfall.toString(),
          `${progressBar} ${coverage}%`
        ]);
      });
      
      const overviewSheet = XLSX.utils.aoa_to_sheet(overviewData);
      
      // Style the overview sheet
      const overviewRange = XLSX.utils.decode_range(overviewSheet['!ref'] || 'A1');
      
      // Set column widths for overview sheet
      overviewSheet['!cols'] = [
        { width: 25 }, // Job Name
        { width: 20 }, // Ort
        { width: 12 }, // Abdeckung
        { width: 15 }, // Status
        { width: 10 }, // Benötigt
        { width: 12 }, // Angemeldet
        { width: 10 }, // Fehlend
        { width: 30 }  // Progress Bar
      ];

      // Apply styling to overview sheet
      for (let row = overviewRange.s.r; row <= overviewRange.e.r; row++) {
        for (let col = overviewRange.s.c; col <= overviewRange.e.c; col++) {
          const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
          if (!overviewSheet[cellAddress]) continue;
          
          // Header styling
          if (row === 0) {
            overviewSheet[cellAddress].s = {
              font: { bold: true, size: 16, color: { rgb: "FFFFFF" } },
              fill: { bgColor: { indexed: 64 }, fgColor: { rgb: "2E7D32" } },
              alignment: { horizontal: "center", vertical: "center" }
            };
          }
          // Section headers
          else if (row === 3 || row === 10) {
            overviewSheet[cellAddress].s = {
              font: { bold: true, size: 14, color: { rgb: "FFFFFF" } },
              fill: { bgColor: { indexed: 64 }, fgColor: { rgb: "1976D2" } },
              alignment: { horizontal: "left", vertical: "center" }
            };
          }
          // Table headers
          else if (row === 11) {
            overviewSheet[cellAddress].s = {
              font: { bold: true, color: { rgb: "FFFFFF" } },
              fill: { bgColor: { indexed: 64 }, fgColor: { rgb: "455A64" } },
              alignment: { horizontal: "center", vertical: "center" }
            };
          }
        }
      }
      
      XLSX.utils.book_append_sheet(workbook, overviewSheet, 'Übersicht');
      
      // Create detailed sheets for each job with new structure
      filteredJobs.forEach(job => {
        const jobData: any[][] = [
          [`🎯 ${job.name.toUpperCase()} - ${job.location}`, '', '', '', '', '', '', '', ''],
          ['', '', '', '', '', '', '', '', ''],
          ['📈 JOB STATISTIKEN', '', '', '', '', '', '', '', ''],
          ['Abdeckung:', `${Math.round(job.coverage)}%`, '', 'Positionen:', `${job.registeredVolunteerCount}/${job.requiredVolunteerCount}`, '', 'Status:', job.coverage >= 80 ? '✅ Vollständig' : job.coverage >= 50 ? '⚠️ Teilweise' : '🔴 Kritisch', ''],
          ['Benötigte Zeit:', formatMinutes(job.totalMinutesRequired), '', 'Zugesagte Zeit:', formatMinutes(job.totalMinutesCommitted), '', 'Fehlende Helfer:', job.shortfall.toString(), ''],
          ['Progress:', '█'.repeat(Math.round(job.coverage / 5)) + '░'.repeat(20 - Math.round(job.coverage / 5)), `${Math.round(job.coverage)}%`, '', '', '', '', '', ''],
          ['', '', '', '', '', '', '', '', ''],          ['📅 DETAILLIERTE ZEITSLOT ÜBERSICHT', '', '', '', '', '', '', '', '', '', ''],
          ['Datum', 'Wochentag', 'Zeit', 'Ort', 'Beschreibung', 'Status', 'Position', 'Helfer Name', 'E-Mail', 'Telefon', 'Organisation'],
        ];
        
        // Group timeslots by date
        const timeslotsByDate = new Map<string, typeof job.timeslots>();
        job.timeslots.forEach(timeslot => {
          const dateKey = timeslot.dateFormatted;
          if (!timeslotsByDate.has(dateKey)) {
            timeslotsByDate.set(dateKey, []);
          }
          timeslotsByDate.get(dateKey)!.push(timeslot);
        });        // Add timeslot data with volunteers as individual rows
        Array.from(timeslotsByDate.entries())
          .sort(([a], [b]) => new Date(a).getTime() - new Date(b).getTime())
          .forEach(([dateKey, timeslots]) => {
            const formattedDate = format(new Date(dateKey), 'dd.MM.yyyy');
            const germanWeekdays = ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'];
            const dayOfWeek = germanWeekdays[new Date(dateKey).getDay()];
            
            timeslots
              .sort((a, b) => a.timeFormatted.localeCompare(b.timeFormatted))
              .forEach(timeslot => {
                const shortfall = timeslot.shortfall > 0 ? timeslot.shortfall : 0;
                const isFull = shortfall === 0;
                const statusIcon = isFull ? '✅' : shortfall <= 2 ? '⚠️' : '🔴';
                const statusText = isFull ? 'Voll besetzt' : `${shortfall} fehlend`;
                  // If there are volunteers, create a row for each
                if (timeslot.volunteers && timeslot.volunteers.length > 0) {
                  timeslot.volunteers.forEach((volunteer, index) => {
                    jobData.push([
                      index === 0 ? formattedDate : '',
                      index === 0 ? dayOfWeek : '',
                      index === 0 ? timeslot.timeFormatted : '',
                      index === 0 ? job.location : '',
                      index === 0 ? (timeslot.description || '') : '',
                      index === 0 ? `${statusIcon} ${statusText}` : '',
                      `${index + 1}/${timeslot.requiredVolunteers}`,
                      volunteer.name,
                      volunteer.email || '',
                      volunteer.phoneNumber || '',
                      volunteer.commodity || ''
                    ]);
                  });
                  
                  // Add empty positions if needed
                  for (let i = timeslot.volunteers.length; i < timeslot.requiredVolunteers; i++) {
                    jobData.push([
                      '',
                      '',
                      '',
                      '',
                      '',
                      '',
                      `${i + 1}/${timeslot.requiredVolunteers}`,
                      '❌ OFFEN',
                      '',
                      '',
                      ''
                    ]);
                  }
                } else {
                  // No volunteers - show empty positions
                  for (let i = 0; i < timeslot.requiredVolunteers; i++) {
                    jobData.push([
                      i === 0 ? formattedDate : '',
                      i === 0 ? dayOfWeek : '',
                      i === 0 ? timeslot.timeFormatted : '',
                      i === 0 ? job.location : '',
                      i === 0 ? (timeslot.description || '') : '',
                      i === 0 ? `${statusIcon} ${statusText}` : '',
                      `${i + 1}/${timeslot.requiredVolunteers}`,
                      '❌ OFFEN',
                      '',
                      '',
                      ''
                    ]);
                  }
                }
                
                // Add separator between timeslots
                jobData.push(['', '', '', '', '', '', '', '', '', '', '']);
              });
          });
        
        const jobSheet = XLSX.utils.aoa_to_sheet(jobData);
          // Set column widths for job sheet
        jobSheet['!cols'] = [
          { width: 12 }, // Datum
          { width: 12 }, // Wochentag  
          { width: 12 }, // Zeit
          { width: 15 }, // Ort
          { width: 20 }, // Beschreibung
          { width: 15 }, // Status
          { width: 8 },  // Position
          { width: 25 }, // Helfer Name
          { width: 25 }, // E-Mail
          { width: 15 }, // Telefon
          { width: 20 }  // Organisation
        ];
        
        // Apply styling to job sheet
        const jobRange = XLSX.utils.decode_range(jobSheet['!ref'] || 'A1');
        
        for (let row = jobRange.s.r; row <= jobRange.e.r; row++) {
          for (let col = jobRange.s.c; col <= jobRange.e.c; col++) {
            const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
            if (!jobSheet[cellAddress]) continue;
            
            const cellValue = jobSheet[cellAddress].v;
            
            // Header styling
            if (row === 0) {
              jobSheet[cellAddress].s = {
                font: { bold: true, size: 16, color: { rgb: "FFFFFF" } },
                fill: { bgColor: { indexed: 64 }, fgColor: { rgb: "2E7D32" } },
                alignment: { horizontal: "center", vertical: "center" }
              };
            }
            // Section headers
            else if (row === 2 || row === 7) {
              jobSheet[cellAddress].s = {
                font: { bold: true, size: 12, color: { rgb: "FFFFFF" } },
                fill: { bgColor: { indexed: 64 }, fgColor: { rgb: "1976D2" } },
                alignment: { horizontal: "left", vertical: "center" }
              };
            }
            // Table headers
            else if (row === 8) {
              jobSheet[cellAddress].s = {
                font: { bold: true, color: { rgb: "FFFFFF" } },
                fill: { bgColor: { indexed: 64 }, fgColor: { rgb: "455A64" } },
                alignment: { horizontal: "center", vertical: "center" }
              };
            }
            // Color code volunteer rows
            else if (row > 8 && cellValue) {
              if (typeof cellValue === 'string' && cellValue.includes('❌ OFFEN')) {
                jobSheet[cellAddress].s = {
                  fill: { bgColor: { indexed: 64 }, fgColor: { rgb: "FFEBEE" } },
                  font: { color: { rgb: "C62828" } }
                };
              } else if (typeof cellValue === 'string' && cellValue.includes('✅')) {
                jobSheet[cellAddress].s = {
                  fill: { bgColor: { indexed: 64 }, fgColor: { rgb: "E8F5E8" } },
                  font: { color: { rgb: "2E7D32" } }
                };
              } else if (typeof cellValue === 'string' && cellValue.includes('⚠️')) {
                jobSheet[cellAddress].s = {
                  fill: { bgColor: { indexed: 64 }, fgColor: { rgb: "FFF3E0" } },
                  font: { color: { rgb: "F57C00" } }
                };
              }
            }
          }
        }
          // Sanitize sheet name (Excel has restrictions)
        const sanitizedName = job.name
          .replace(/[\\/?*[\]:]/g, '-')
          .substring(0, 31);
        
        XLSX.utils.book_append_sheet(workbook, jobSheet, sanitizedName);
      });
      
      // Generate filename with current date
      const filename = `Strudengauer-Messe-Export-${format(new Date(), 'yyyy-MM-dd-HHmm')}.xlsx`;
      
      // Write and download file
      XLSX.writeFile(workbook, filename);
    } catch (error) {
      console.error('Error generating Excel export:', error);
      setError('Fehler beim Erstellen der Excel-Datei. Bitte versuchen Sie es erneut.');
    }
  };
  
  // Handle Word document export with proper tables for each job
  const handleWordExport = async () => {
    try {
      // Create document with each job on a separate page
      const doc = new Document({
        creator: "Strudengauer Messe App",
        title: "Dienstplan und Helferliste",
        description: "Übersicht aller Dienste, Zeitslots und angemeldeten Helfer",
        sections: [{
          properties: {
            page: {
              size: {
                orientation: PageOrientation.LANDSCAPE,
              },
              margin: {
                top: 1000,
                right: 1000,
                bottom: 1000,
                left: 1000,
              },
            },
          },
          children: generateWordJobPages(),
        }],
      });

      // Generate and save document
      const blob = await Packer.toBlob(doc);
      saveAs(blob, `Strudengauer-Messe-Dienstplan-${format(new Date(), 'yyyy-MM-dd-HHmm')}.docx`);
    } catch (error) {
      console.error('Error generating Word document:', error);
      setError('Fehler beim Erstellen des Word-Dokuments. Bitte versuchen Sie es erneut.');
    }
  };

  // Helper function to generate job pages with proper tables
  const generateWordJobPages = () => {
    const content: any[] = [];

    filteredJobs.forEach((job, jobIndex) => {
      // Add page break before each job (except the first one)
      if (jobIndex > 0) {
        content.push(
          new Paragraph({ 
            text: "",
            pageBreakBefore: true 
          })
        );
      }

      // Job title
      const jobTitle = `${job.name} - ${job.location}`;
        
      content.push(
        new Paragraph({
          text: jobTitle,
          heading: HeadingLevel.HEADING_1,
          alignment: AlignmentType.CENTER,
        }),
         new Paragraph({
          text: `Verantwortlich: ${job.responsible || 'Nicht angegeben'}`,
          alignment: AlignmentType.CENTER,
          heading: HeadingLevel.HEADING_3,
        }),
        new Paragraph({
          text: `Abdeckung: ${Math.round(job.coverage)}% | Positionen: ${job.registeredVolunteerCount}/${job.requiredVolunteerCount} | Status: ${job.shortfall > 0 ? `${job.shortfall} mehr benötigt` : 'Vollständig besetzt'}`,
          alignment: AlignmentType.CENTER,
        }),
        new Paragraph({ text: "" })
      );

      // Group timeslots by date
      const timeslotsByDate = new Map<string, typeof job.timeslots>();
      job.timeslots.forEach(timeslot => {
        const dateKey = timeslot.dateFormatted;
        if (!timeslotsByDate.has(dateKey)) {
          timeslotsByDate.set(dateKey, []);
        }
        timeslotsByDate.get(dateKey)!.push(timeslot);
      });

      // Process each date
      Array.from(timeslotsByDate.entries())
        .sort(([a], [b]) => new Date(a).getTime() - new Date(b).getTime())
        .forEach(([dateKey, timeslots], dateIndex) => {
          const formattedDate = format(new Date(dateKey), 'dd.MM.yyyy');
          const germanWeekdays = ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'];
          const dayOfWeek = germanWeekdays[new Date(dateKey).getDay()];

          // Add some space between dates
          if (dateIndex > 0) {
            content.push(new Paragraph({ text: "" }));
          }

          content.push(
            new Paragraph({
              text: `${formattedDate} (${dayOfWeek})`,
              heading: HeadingLevel.HEADING_2,
            })
          );

          // Sort timeslots by time
          timeslots.sort((a, b) => a.timeFormatted.localeCompare(b.timeFormatted));

          // Create table for all timeslots of this date
          timeslots.forEach(timeslot => {
            content.push(
              new Paragraph({
                text: `Zeitslot: ${timeslot.timeFormatted}${timeslot.description ? ` - ${timeslot.description}` : ''}`,
                heading: HeadingLevel.HEADING_3,
              })
            );

            // Create table rows for this timeslot
            const tableRows = [
              // Header row
              new DocxTableRow({
                tableHeader: true,
                children: [
                  new DocxTableCell({
                    children: [new Paragraph({ text: "Position" })],
                    width: { size: 15, type: WidthType.PERCENTAGE },
                    shading: { fill: "E0E0E0" },
                  }),
                  new DocxTableCell({
                    children: [new Paragraph({ text: "Name" })],
                    width: { size: 30, type: WidthType.PERCENTAGE },
                    shading: { fill: "E0E0E0" },
                  }),
                  new DocxTableCell({
                    children: [new Paragraph({ text: "Telefon" })],
                    width: { size: 25, type: WidthType.PERCENTAGE },
                    shading: { fill: "E0E0E0" },
                  }),
                  new DocxTableCell({
                    children: [new Paragraph({ text: "Email" })],
                    width: { size: 30, type: WidthType.PERCENTAGE },
                    shading: { fill: "E0E0E0" },
                  }),
                ],
              }),
            ];

            // Add all required positions (filled and empty)
            // Print all registered volunteers first
            timeslot.volunteers.forEach((volunteer, i) => {
              const positionNumber = i + 1;
              tableRows.push(
                new DocxTableRow({
                  children: [
                    new DocxTableCell({
                      children: [new Paragraph(`${positionNumber}/${timeslot.requiredVolunteers}`)],
                    }),
                    new DocxTableCell({
                      children: [new Paragraph(volunteer.name)],
                    }),
                    new DocxTableCell({
                      children: [new Paragraph("")],
                    }),
                    new DocxTableCell({
                      children: [new Paragraph("")],
                    }),
                  ],
                })
              );
            });

            // Fill empty positions if needed
            for (let i = timeslot.volunteers.length; i < timeslot.requiredVolunteers; i++) {
              const positionNumber = i + 1;
              tableRows.push(
                new DocxTableRow({
                  children: [
                    new DocxTableCell({
                      children: [new Paragraph(`${positionNumber}/${timeslot.requiredVolunteers}`)],
                    }),
                    new DocxTableCell({
                      children: [new Paragraph("_________________________")],
                    }),
                    new DocxTableCell({
                      children: [new Paragraph("_________________________")],
                    }),
                    new DocxTableCell({
                      children: [new Paragraph("_________________________")],
                    }),
                  ],
                })
              );
            }

            
           

            // Create the table
            const table = new DocxTable({
              width: { size: 100, type: WidthType.PERCENTAGE },
              borders: {
                top: { style: BorderStyle.SINGLE, size: 1, color: "000000" },
                bottom: { style: BorderStyle.SINGLE, size: 1, color: "000000" },
                left: { style: BorderStyle.SINGLE, size: 1, color: "000000" },
                right: { style: BorderStyle.SINGLE, size: 1, color: "000000" },
                insideHorizontal: { style: BorderStyle.SINGLE, size: 1, color: "CCCCCC" },
                insideVertical: { style: BorderStyle.SINGLE, size: 1, color: "CCCCCC" },
              },
              rows: tableRows,
            });

            content.push(table);
            content.push(new Paragraph({ text: "" })); // Space after table
          });
        });
    });

    return content;
  };
  
  // Handle opening volunteer dialog
  const handleOpenVolunteerDialog = (volunteers: Volunteer[], jobName: string, timeslot: string) => {
    setSelectedVolunteers(volunteers);
    setSelectedTimeslotInfo({ jobName, timeslot });
    setDialogOpen(true);
  };
  
  // Handle closing volunteer dialog
  const handleCloseVolunteerDialog = () => {
    setDialogOpen(false);
  };
  
  // Handle print mode
  const handlePrint = () => {
    setPrintMode(true);
    setTimeout(() => {
      window.print();
      setPrintMode(false);
    }, 500);
  };
  
  // Handle job roster print mode
  const handleRosterPrint = () => {
    setRosterPrintMode(true);
    setTimeout(() => {
      window.print();
      setRosterPrintMode(false);
    }, 500);
  };
  
  // Render coverage indicator
  const renderCoverageIndicator = (coverage: number) => {
    let color = 'success';
    if (coverage < 50) {
      color = 'error';
    } else if (coverage < 80) {
      color = 'warning';
    }
    
    return (
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <LinearProgress
          variant="determinate"
          value={Math.min(100, coverage)}
          color={color as 'error' | 'warning' | 'success'}
          sx={{ 
            flexGrow: 1, 
            mr: 1, 
            height: 10, 
            borderRadius: 5
          }}
        />
        <Typography variant="body2" color="text.secondary">
          {Math.round(coverage)}%
        </Typography>
      </Box>
    );
  };
  
  // Handle Special Roles export (Ordner and Ersthelfer)
  const handleSpecialRolesExport = async () => {
    try {
      // Collect all volunteers with special roles, grouped by role and day
      const specialRoles: { [role: string]: { [date: string]: any[] } } = {};
      filteredJobs.forEach(job => {
        job.timeslots.forEach(timeslot => {
          const dateKey = timeslot.dateFormatted;
          timeslot.volunteers.forEach(volunteer => {
            if (volunteer.isOrdner) {
              if (!specialRoles['Ordner']) specialRoles['Ordner'] = {};
              if (!specialRoles['Ordner'][dateKey]) specialRoles['Ordner'][dateKey] = [];
              specialRoles['Ordner'][dateKey].push({
                ...volunteer,
                jobName: job.name,
                registrationTime: timeslot.timeFormatted,
                jobDate: dateKey
              });
            }
            if (volunteer.isErsthelfer) {
              if (!specialRoles['Ersthelfer']) specialRoles['Ersthelfer'] = {};
              if (!specialRoles['Ersthelfer'][dateKey]) specialRoles['Ersthelfer'][dateKey] = [];
              specialRoles['Ersthelfer'][dateKey].push({
                ...volunteer,
                jobName: job.name,
                registrationTime: timeslot.timeFormatted,
                jobDate: dateKey
              });
            }
          });
        });
      });

      if (Object.keys(specialRoles).length === 0) {
        setError('Keine Ordner oder Ersthelfer gefunden.');
        return;
      }

      // Build Word document content
      const content: any[] = [];
      const germanWeekdays = ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'];

      Object.entries(specialRoles).forEach(([role, days], roleIdx) => {
        Object.entries(days).sort(([a], [b]) => new Date(a).getTime() - new Date(b).getTime()).forEach(([dateKey, volunteers], dayIdx) => {
          const dateObj = new Date(dateKey);
          const heading = `${role} ${germanWeekdays[dateObj.getDay()]} ${format(dateObj, 'dd.MM.yyyy')}`;
          if (roleIdx > 0 || dayIdx > 0) {
            content.push(new Paragraph({ text: '', pageBreakBefore: true }));
          }
          content.push(new Paragraph({
            text: heading,
            heading: HeadingLevel.HEADING_1,
            alignment: AlignmentType.LEFT,
          }));

          // Table header
          const tableRows = [
            new DocxTableRow({
              tableHeader: true,
              children: [
                new DocxTableCell({ children: [new Paragraph({ text: 'Name' })] }),
                new DocxTableCell({ children: [new Paragraph({ text: 'Telefon' })] }),
                new DocxTableCell({ children: [new Paragraph({ text: 'E-Mail' })] }),
                new DocxTableCell({ children: [new Paragraph({ text: 'Job' })] }),
                new DocxTableCell({ children: [new Paragraph({ text: 'Zeit' })] }),
              ],
            })
          ];

          // Table rows
          volunteers.forEach(v => {
            tableRows.push(new DocxTableRow({
              children: [
                new DocxTableCell({ children: [new Paragraph(v.name)] }),
                new DocxTableCell({ children: [new Paragraph(v.phoneNumber || '')] }),
                new DocxTableCell({ children: [new Paragraph(v.email || '')] }),
                new DocxTableCell({ children: [new Paragraph(v.jobName)] }),
                new DocxTableCell({ children: [new Paragraph(v.registrationTime)] }),
              ]
            }));
          });

          // Create the table
          const table = new DocxTable({
            width: { size: 100, type: WidthType.PERCENTAGE },
            borders: {
              top: { style: BorderStyle.SINGLE, size: 1, color: "000000" },
              bottom: { style: BorderStyle.SINGLE, size: 1, color: "000000" },
              left: { style: BorderStyle.SINGLE, size: 1, color: "000000" },
              right: { style: BorderStyle.SINGLE, size: 1, color: "000000" },
              insideHorizontal: { style: BorderStyle.SINGLE, size: 1, color: "CCCCCC" },
              insideVertical: { style: BorderStyle.SINGLE, size: 1, color: "CCCCCC" },
            },
            rows: tableRows,
          });
          content.push(table);
        });
      });

      // Create Word document
      const doc = new Document({
        creator: "Strudengauer Messe App",
        title: "Ordner und Ersthelfer - Strudengauer Messe",
        description: "Übersicht aller Ordner und Ersthelfer nach Tag und Rolle",
        sections: [{
          properties: {
            page: {
              size: {
                orientation: PageOrientation.LANDSCAPE,
              },
              margin: {
                top: 1000,
                right: 1000,
                bottom: 1000,
                left: 1000,
              },
            },
          },
          children: content,
        }],
      });

      // Generate and save document
      const blob = await Packer.toBlob(doc);
      saveAs(blob, `Strudengauer-Messe-Ordner-Ersthelfer-${format(new Date(), 'yyyy-MM-dd-HHmm')}.docx`);
    } catch (error) {
      console.error('Error generating Special Roles document:', error);
      setError('Fehler beim Erstellen des Ordner/Ersthelfer-Dokuments. Bitte versuchen Sie es erneut.');
    }
  };


  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="body1" sx={{ mt: 2 }}>
          Loading dashboard data...
        </Typography>
      </Container>
    );
  }
  
  if (error) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }
  
  return (
    <Container maxWidth="xl" className={(printMode || rosterPrintMode) ? 'print-mode' : ''}>
      <Box sx={{ mt: 4, mb: 4 }} className={rosterPrintMode ? 'no-print' : ''}>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          mb: 3,
          flexWrap: 'wrap'
        }}>
          <Typography variant="h4" component="h1" gutterBottom className="print-title">
            Job-Übersicht & Analyse
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }} className="no-print">
            <Button 
              variant="outlined" 
              startIcon={<PrintIcon />}
              onClick={handlePrint}
            >
              Drucken
            </Button>
            <Button 
              variant="outlined" 
              color="secondary"
              startIcon={<DocumentScanner />}
              onClick={handleRosterPrint}
              title="Erstellt eine Tabelle pro Dienst mit Zeitslots und Helfern zum Ausdrucken"
            >
              Dienstplan drucken
            </Button>
            <Button 
              variant="outlined" 
              color="primary"
              startIcon={<FileDownloadIcon />}
              onClick={handleExcelExport}
              title="Exportiert die Daten als Excel-Datei"
            >
              Excel exportieren
            </Button>
            <Button 
              variant="outlined" 
              color="success"
              startIcon={<DescriptionIcon />}
              onClick={handleWordExport}
              title="Erstellt ein Word-Dokument mit Tabellen für Zeitslots und Helfer"
            >
              Word-Export
            </Button>
            <Button 
              variant="outlined" 
              color="info"
              startIcon={<DescriptionIcon />}
              onClick={handleSpecialRolesExport}
              title="Erstellt ein Word-Dokument mit Ordner und Ersthelfer"
            >
              Ordner/Ersthelfer-Export
            </Button>
          </Box>
        </Box>
          {/* Summary Cards */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr 1fr' }, gap: 3, mb: 4 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Gesamt-Abdeckung</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  {overallStats.overallCoverage < 50 && <WarningIcon color="error" sx={{ mr: 1 }} />}
                  <Typography variant="h4">
                    {Math.round(overallStats.overallCoverage)}%
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {overallStats.totalRegisteredVolunteers} von {overallStats.totalRequiredVolunteers} Positionen besetzt
                </Typography>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Kritische Dienste</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <WarningIcon color="error" sx={{ mr: 1 }} />
                  <Typography variant="h4">
                    {overallStats.criticalJobs}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Dienste mit weniger als 50% Abdeckung
                </Typography>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Helferstunden benötigt</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <AccessTimeIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="h4">
                    {Math.round(overallStats.totalHoursRequired)}h
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Insgesamt benötigte Arbeitsstunden
                </Typography>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Abdeckung Stunden</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <AccessTimeIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="h4">
                    {Math.round(overallStats.totalHoursCommitted)}h
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {Math.round((overallStats.totalHoursCommitted / overallStats.totalHoursRequired) * 100)}% der benötigten Stunden
                </Typography>
              </CardContent>
            </Card>
          </Box>
        
        {/* Filters */}
        <Paper sx={{ p: 2, mb: 3 }} className="no-print">
          <Box sx={{ 
            display: 'flex', 
            gap: 2, 
            alignItems: 'center',
            flexWrap: 'wrap'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <FilterListIcon sx={{ mr: 1 }} />
              <Typography variant="body1">Filter:</Typography>
            </Box>
            
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel id="job-filter-label">Dienst</InputLabel>
              <Select
                labelId="job-filter-label"
                id="job-filter"
                value={jobFilter}
                label="Dienst"
                onChange={(e) => setJobFilter(e.target.value)}
              >
                <MenuItem value="all">Alle Dienste</MenuItem>
                {jobs.map((job) => (
                  <MenuItem key={job.id} value={job.id}>{job.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel id="day-filter-label">Tag</InputLabel>
              <Select
                labelId="day-filter-label"
                id="day-filter"
                value={dayFilter}
                label="Tag"
                onChange={(e) => setDayFilter(e.target.value)}
              >
                <MenuItem value="all">Alle Tage</MenuItem>
                {availableDays.map((day) => (
                  <MenuItem key={day} value={day}>
                    {format(new Date(day), 'dd.MM.yyyy EEEE')}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel id="coverage-filter-label">Abdeckungsgrad</InputLabel>
              <Select
                labelId="coverage-filter-label"
                id="coverage-filter"
                value={coverageFilter}
                label="Abdeckungsgrad"
                onChange={(e) => setCoverageFilter(e.target.value)}
              >
                <MenuItem value="all">Alle</MenuItem>
                <MenuItem value="critical">Kritisch (&lt;50%)</MenuItem>
                <MenuItem value="warning">Warnung (50-80%)</MenuItem>
                <MenuItem value="good">Gut (&gt;80%)</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Paper>
        
        {/* Job list */}
        {filteredJobs.length === 0 ? (
          <Alert severity="info">
            Keine Dienste entsprechen den ausgewählten Filtern.
          </Alert>
        ) : (
          <Box>
            {filteredJobs.map((job) => (
              <Paper key={job.id} sx={{ p: 3, mb: 3 }}>
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'flex-start',
                  mb: 2,
                  flexWrap: 'wrap'
                }}>
                  <Box>
                    <Typography variant="h5">{job.name}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {job.location} | {job.timeslotCount} Schicht{job.timeslotCount !== 1 ? 'en' : ''}
                    </Typography>
                  </Box>
                  
                  <Box className="no-print">
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => navigate(`/admin/jobs/${job.id}`)}
                    >
                      Details
                    </Button>
                  </Box>
                </Box>
                
                <Box sx={{ 
                  display: 'grid', 
                  gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, 
                  gap: 2,
                  mb: 2 
                }}>
                  <Box>
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="body2" component="span">Abdeckung: </Typography>
                      <Typography variant="body1" component="span" fontWeight="bold">
                        {job.registeredVolunteerCount} von {job.requiredVolunteerCount} Positionen
                      </Typography>
                    </Box>
                    {renderCoverageIndicator(job.coverage)}
                  </Box>
                  
                  <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2">
                        Benötigte Zeit: <strong>{formatMinutes(job.totalMinutesRequired)}</strong>
                      </Typography>
                      <Typography variant="body2">
                        Zugesagte Zeit: <strong>{formatMinutes(job.totalMinutesCommitted)}</strong>
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={Math.min(100, (job.totalMinutesCommitted / job.totalMinutesRequired) * 100)}
                      color={job.coverage < 50 ? 'error' : job.coverage < 80 ? 'warning' : 'success'}
                      sx={{ height: 10, borderRadius: 5 }}
                    />
                  </Box>
                </Box>

                <Divider sx={{ my: 2 }} />
                
                <Typography variant="h6" sx={{ mb: 2 }}>Schichten</Typography>
                
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
                  <Box>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Datum</TableCell>
                            <TableCell>Zeit</TableCell>
                            <TableCell>Benötigt</TableCell>
                            <TableCell>Angemeldet</TableCell>
                            <TableCell>Offen</TableCell>
                            <TableCell>Abdeckung</TableCell>
                            <TableCell>Helfer</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {job.timeslots.map((timeslot) => (
                            <TableRow 
                              key={timeslot.id}
                              sx={{ 
                                backgroundColor: timeslot.filledSpots < 50 
                                  ? 'rgba(255, 0, 0, 0.05)' 
                                  : timeslot.filledSpots < 80 
                                    ? 'rgba(255, 153, 0, 0.05)' 
                                    : 'rgba(0, 255, 0, 0.05)'
                              }}
                            >
                              <TableCell>
                                {format(new Date(timeslot.dateFormatted), 'dd.MM.yyyy')}
                                <Typography variant="caption" display="block">
                                  {format(new Date(timeslot.dateFormatted), 'EEEE')}
                                </Typography>
                              </TableCell>
                              <TableCell>{timeslot.timeFormatted}</TableCell>
                              <TableCell>{timeslot.requiredVolunteers}</TableCell>
                              <TableCell>{timeslot.filledSpots || 0}</TableCell>
                              <TableCell>
                                {timeslot.shortfall > 0 ? (
                                  <Typography color="error">{timeslot.shortfall}</Typography>
                                ) : (
                                  <CheckCircleIcon color="success" fontSize="small" />
                                )}
                              </TableCell>
                              <TableCell>
                                <Box sx={{ minWidth: 100 }}>
                                  {renderCoverageIndicator((timeslot.filledSpots / timeslot.requiredVolunteers) * 100)}
                                </Box>
                              </TableCell>                              <TableCell sx={{ maxWidth: { xs: '120px', sm: '180px' } }}>                                {timeslot.volunteers.length === 0 ? (
                                  <Button
                                    variant="text" 
                                    size="small"
                                    disabled
                                    sx={{ p: 0, textAlign: 'left', textTransform: 'none', color: 'text.secondary' }}
                                  >
                                    <Typography noWrap variant="body2">Keine Helfer</Typography>
                                  </Button>
                                ) : (
                                  // Show button with count for more than 5 volunteers
                                  <Button
                                    variant="contained"
                                    size="small"
                                    color="primary"
                                    onClick={() => handleOpenVolunteerDialog(timeslot.volunteers, job.name, timeslot.timeFormatted)}
                                    startIcon={<PersonIcon />}
                                    sx={{ minWidth: '100px', fontSize: '0.75rem' }}
                                  >
                                    {timeslot.volunteers.length} Helfer anzeigen
                                  </Button>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                  <Box>
                    {/* Second table content */}
                  </Box>
                </Box>
              </Paper>
            ))}
          </Box>
        )}      </Box>
      
      {/* Volunteer Detail Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseVolunteerDialog}
        aria-labelledby="volunteer-dialog-title"
        maxWidth="sm"
        fullWidth
      >        <DialogTitle id="volunteer-dialog-title" sx={{ 
          display: 'flex', 
          justifyContent: 'space-between',
          alignItems: 'center', 
          pb: 1,
          bgcolor: 'primary.main',
          color: 'primary.contrastText'
        }}>
          <Box>
            <Typography variant="h6" component="div">
              <PersonIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Helfer für {selectedTimeslotInfo?.jobName}
            </Typography>
            <Typography variant="subtitle2" sx={{ opacity: 0.9, mt: 0.5 }}>
              {selectedTimeslotInfo?.timeslot}
            </Typography>
          </Box>
          <IconButton 
            edge="end" 
            color="inherit" 
            onClick={handleCloseVolunteerDialog}
            aria-label="close"
          >
            <Close />
          </IconButton>
        </DialogTitle><DialogContent dividers>
          {selectedVolunteers.length > 0 && (
            <>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
                {selectedVolunteers.length} {selectedVolunteers.length === 1 ? 'Helfer' : 'Helfer'} zugewiesen
              </Typography>
              <Box sx={{ 
                maxHeight: '400px', 
                overflow: 'auto',
                pb: 1
              }}>                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {selectedVolunteers.map((volunteer, index) => (
                    <Box key={volunteer.id}>
                      <Paper elevation={1} sx={{ p: 2, borderRadius: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', flexWrap: 'wrap' }}>
                          <Avatar 
                            sx={{ 
                              bgcolor: `hsl(${volunteer.name.length * 10}, 70%, 50%)`,
                              mr: 2,
                              mb: 1,
                              width: 56,
                              height: 56,
                              fontSize: '1.5rem'
                            }}
                          >
                            {volunteer.name.charAt(0).toUpperCase()}
                          </Avatar>
                          
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="h6" gutterBottom sx={{ mt: 0.5 }}>
                              {volunteer.name}
                            </Typography>
                            
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, flexWrap: 'wrap' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', mr: 3, mb: 1 }}>
                                <EmailIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
                                <Typography variant="body2">
                                  {volunteer.email || 'Keine E-Mail'}
                                </Typography>
                              </Box>
                              
                              {volunteer.phoneNumber && (
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                  <PhoneIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
                                  <Typography variant="body2">
                                    {volunteer.phoneNumber}
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                            
                            {volunteer.commodity && (
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <BusinessIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
                                <Typography variant="body2">
                                  {volunteer.commodity}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </Box>
                                             </Paper>
                    </Box>
                  ))}
                </Box>
              </Box>
            </>
          )}
        </DialogContent><DialogActions sx={{ justifyContent: 'space-between' }}>
          <Button 
            onClick={() => {
              if (selectedVolunteers.length === 1) {
                navigate(`/admin/volunteers/${selectedVolunteers[0].id}`);
              }
            }}
            disabled={selectedVolunteers.length !== 1}
          >
            Details anzeigen
          </Button>
          <Button onClick={handleCloseVolunteerDialog}>Schließen</Button>
        </DialogActions>
      </Dialog>
      {/* Job Roster Print View - Only shown when rosterPrintMode is true */}
      {rosterPrintMode && (
        <Box className="roster-print-container" sx={{ display: 'none', '@media print': { display: 'block' } }}>          <Typography variant="h4" sx={{ textAlign: 'center', mb: 1 }}>
            Dienst-Einsatzplan
          </Typography>
          <Typography variant="body2" sx={{ textAlign: 'center', mb: 3, color: 'text.secondary' }}>
            Stand: {format(new Date(), 'dd.MM.yyyy HH:mm')} Uhr
          </Typography>
            {filteredJobs.map((job) => (
            <Box key={job.id} sx={{ mb: 6 }}>              <Box sx={{ 
                backgroundColor: '#f5f5f5',
                p: 1,
                mb: 2,
                border: '1px solid #ddd',
                borderRadius: '4px'
              }}>
                <Typography variant="h5" sx={{ fontWeight: 'bold', textAlign: 'center' }}>
                  {job.name} - {job.location}
                </Typography>
              </Box>
                {/* Summary cards like in the dashboard */}
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between',
                mb: 3,
                flexWrap: 'wrap',
                gap: 2
              }}>
                <Paper sx={{ p: 2, flex: '1 0 200px', maxWidth: '300px' }}>
                  <Typography variant="subtitle1" gutterBottom>Abdeckung</Typography>
                  <Typography variant="h5" sx={{ mb: 1 }}>
                    {Math.round(job.coverage)}%
                  </Typography>
                  <Typography variant="body2">
                    {job.registeredVolunteerCount} von {job.requiredVolunteerCount} Positionen
                  </Typography>
                </Paper>
                
                <Paper sx={{ p: 2, flex: '1 0 200px', maxWidth: '300px' }}>
                  <Typography variant="subtitle1" gutterBottom>Zeitaufwand</Typography>
                  <Typography variant="h5" sx={{ mb: 1 }}>
                    {formatMinutes(job.totalMinutesRequired)}
                  </Typography>
                  <Typography variant="body2">
                    Zugesagt: {formatMinutes(job.totalMinutesCommitted)}
                  </Typography>
                </Paper>                  <Paper sx={{ p: 2, flex: '1 0 200px', maxWidth: '300px' }}>
                  <Typography variant="subtitle1" gutterBottom>Status Besetzung</Typography>
                  <Typography variant="h5" sx={{ mb: 1, color: job.shortfall > 0 ? 'error.main' : 'success.main' }}>
                    {job.shortfall > 0 ? `${job.shortfall} mehr benötigt` : 'Vollständig besetzt'}
                  </Typography>
                  <Typography variant="body2">
                    {job.registeredVolunteerCount} von {job.requiredVolunteerCount} Positionen
                    {rosterPrintMode && job.shortfall > 0 && (
                      <Typography variant="caption" sx={{ display: 'block', mt: 1, fontStyle: 'italic' }}>
                        (Neue Helfer im Dienstplan eintragen)
                      </Typography>
                    )}
                  </Typography>
                </Paper>
              </Box>
                {/* Show improved timeslot matrix with timeslots as columns */}        
                      <Typography variant="h6" sx={{ mb: 1 }}>Schichten-Übersicht</Typography>
                {groupTimeslots(job.timeslots).map((timeslotGroup, groupIndex) => (                <Box 
                  key={groupIndex} 
                  data-page-break={groupIndex < Math.ceil(job.timeslots.length / 3) - 1 ? "true" : "false"}
                  sx={{ mb: 4 }}>
                  <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
                    Zeitraum {groupIndex + 1}
                  </Typography>
                  
                  <TableContainer component={Paper} sx={{ mb: 3, maxWidth: '100%' }}>
                    <Table size="small" sx={{ tableLayout: 'fixed' }}>
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                          <TableCell sx={{ width: '120px', fontWeight: 'bold' }}>Position</TableCell>
                          {timeslotGroup.map((timeslot) => (
                            <TableCell 
                              key={timeslot.id} 
                              sx={{ 
                                fontWeight: 'bold',
                                width: `calc((100% - 120px) / ${timeslotGroup.length})`,
                                fontSize: '0.9rem',
                                p: 1
                              }}
                            >
                              <Box sx={{ 
                                display: 'flex', 
                                flexDirection: 'column'
                              }}>
                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                  {format(new Date(timeslot.dateFormatted), 'EEEE')}
                                </Typography>
                                <Typography variant="body2">
                                  {format(new Date(timeslot.dateFormatted), 'dd.MM.yyyy')}
                                </Typography>
                                <Typography variant="body2">
                                  {timeslot.timeFormatted}
                                </Typography>
                                {timeslot.description && (
                                  <Typography variant="body2" sx={{ fontStyle: 'italic', mt: 0.5 }}>
                                    {timeslot.description}
                                  </Typography>
                                )}
                              </Box>
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{
                          '& > td': {
                            border: '1px solid rgba(0, 0, 0, 0.12)',
                            minHeight: '60px',
                            verticalAlign: 'top'
                          }
                        }}>
                          <TableCell sx={{ fontWeight: 'medium', backgroundColor: '#f9f9f9' }}>
                            Helfer
                          </TableCell>
                          
                          {timeslotGroup.map((timeslot) => (
                            <TableCell
                              key={`${timeslot.id}-volunteers`}
                              sx={{
                                backgroundColor: (timeslot.filledSpots || 0) >= timeslot.requiredVolunteers ? 
                                  'rgba(0, 255, 0, 0.05)' : 
                                  'rgba(255, 0, 0, 0.05)',
                                p: 1
                              }}
                            >
                              <Box>
                                <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 2, textAlign: 'center' }}>
                                  {timeslot.filledSpots || 0} von {timeslot.requiredVolunteers} besetzt
                                </Typography>
                                {timeslot.volunteers.map((volunteer, i) => (
                                  <Box 
                                    key={`filled-${i}`}
                                    sx={{ 
                                      height: '24px',
                                      borderBottom: '1px solid #666',
                                      marginBottom: '8px',
                                      display: 'flex',
                                      alignItems: 'center'
                                    }}
                                  >
                                    <Typography variant="body2" sx={{ width: '24px', color: '#666' }}>
                                      {i + 1}.
                                    </Typography>
                                    <Typography variant="body2" sx={{ flex: 1 }}>
                                      {volunteer.name}
                                    </Typography>
                                  </Box>
                                ))}
                                {Array.from({ length: timeslot.shortfall }).map((_, i) => (
                                  <Box 
                                    key={`empty-${i}`}
                                    sx={{ 
                                      height: '24px',
                                      borderBottom: '1px solid #666',
                                      marginBottom: '8px',
                                      display: 'flex',
                                      alignItems: 'center'
                                    }}
                                  >
                                    <Typography variant="body2" sx={{ width: '24px', color: '#666' }}>
                                      {timeslot.volunteers.length + i + 1}.
                                    </Typography>
                                  </Box>
                                ))}
                              </Box>
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              ))}

              <Typography variant="body2" sx={{ mb: 1 }}>
                Legende: <span style={{ color: 'green' }}>■</span> Vollständig besetzt <span style={{ color: 'red', marginLeft: '10px' }}>■</span> Weitere Helfer benötigt
              </Typography>
            </Box>
          ))}
        </Box>
      )}      <style>
        {`
          @media print {
            /* Ensure tables break correctly */
            .roster-print-container table {
              page-break-inside: avoid;
            }
            
            /* Only force page breaks for the timeslot group boxes that need it */
            .roster-print-container [data-page-break="true"] {
              page-break-after: always;
            }
              /* Prevent unwanted page breaks */
            .roster-print-container > div {
              page-break-after: auto;
              page-break-inside: avoid;
            }
            
            /* Keep job title with its first timeslot group */
            .roster-print-container > div > :first-child {
              page-break-after: avoid;
            }
            
            /* Keep job summary cards together */
            .roster-print-container > div > :nth-child(2) {
              page-break-inside: avoid;
              page-break-after: avoid;
            }
            
            /* Make sure the print layout is optimized */
            @page {
              size: A4 landscape;
              margin: 1cm;
            }
          }
        `}
      </style>
    </Container>
  );
};

export default AdminDashboardPage;
